import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { isEqual, startOfDay } from 'date-fns';

import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { PaymentMethodLogo } from 'shared/contacts';
import { Button, SelectOptionLocked, MultiselectOutsideOptions } from '@/components/UI';
import {
  Box,
  Select,
  Tooltip,
  TextField,
  Radio,
  InteracWordmark,
  withStyles,
  BodyText,
  PaymentMethod,
  FieldData,
  PaymentMethodType,
  NumbersOnly,
} from 'vopay';

import { PaymentFieldDefault } from './types';
import styles from './TransactionCreationFields.module.scss';
import { useHandlePaymentMethodChange } from './useHandlePaymentMethodChange';
import usePaymentMethodCurrencySupport from './usePaymentMethodCurrencySupport';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';

const classes = withStyles(styles);

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());

  return isEqual(date, today);
}

// Scheduling (Recurrence/dates/Ending/etc.) is set up for a specific transaction, not tied to
// any one Payment Method — switching methods (including into/out of the "Request" eLinx flow)
// must not carry a stale schedule over into the newly-selected method's form.
function scheduleResetValues(): Partial<PaymentFieldDefault> {
  const today = String(Date.now());

  return {
    ScheduleType: 'single',
    Recurrence: 'one-time',
    TakeFromAccountOn: today,
    StartDate: today,
    Frequency: undefined,
    Interval: undefined,
    ByDay: undefined,
    SemiMonthlyDates: '',
    Ending: 'End Date',
    EndDate: '',
    EndingAfterPayments: '',
  };
}

const SCHEDULE_RESET_TOUCHED = {
  ScheduleType: false,
  Recurrence: false,
  TakeFromAccountOn: false,
  StartDate: false,
  Frequency: false,
  Interval: false,
  ByDay: false,
  SemiMonthlyDates: false,
  Ending: false,
  EndDate: false,
  EndingAfterPayments: false,
};

export function usePaymentSettingsFields(
  formType: 'Pay' | 'Collect',
  country: string,
  paymentMethods: PaymentMethod[],
): FieldData<PaymentFieldDefault>[] {
  type PaymentSelectionMethodOption = {
    label: string;
    value: string;
    featureKey: string;
    visible: boolean;
    renderLabel?: React.ReactNode;
  };
  type BankingPaymentRailOption = {
    label: React.ReactNode;
    value: string;
    featureKey: string;
    visible: boolean;
    isRecurringEnabled: boolean;
  };
  const { PayLinkEnabled } = useLockedFeatures();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const handlePaymentMethodChange = useHandlePaymentMethodChange();
  const { isSelectionMethodAvailable, isPaymentMethodAvailable } = usePaymentMethodCurrencySupport(formType);
  const [, , , fetchClientBankAccounts] = useFetchClientBankAccounts({});

  const {
    response: { clientAccountsEnabled, Country },
  } = useClientData();

  const [isNewPaymentMethod, setIsNewPaymentMethod] = useState(false);
  const { permissions } = usePermissions();

  // Helper function to map PaymentMethod types to feature keys
  const getPaymentMethodFeatureKey = (paymentMethod: PaymentMethodType): string => {
    const featureKeyMap: { [key: string]: string } = {
      BankAccount: 'Bank Account',
      Email: formType === 'Pay' ? 'interacEtransfer' : 'interacEtransferMoneyRequest',
      PhoneNumber: formType === 'Pay' ? 'interacEtransfer' : 'interacEtransferMoneyRequest',
      CreditCard: 'Credit Card',
      DebitCard: formType === 'Pay' ? 'OCT' : 'AFT',
      PayPal: 'PayPal Withdraw',
      Venmo: 'Venmo Withdraw',
      Zelle: 'Zelle Withdraw',
      GooglePay: 'Google Pay',
      ApplePay: 'Apple Pay',
    };

    return featureKeyMap[paymentMethod] || paymentMethod;
  };

  // A pay-link collection cannot take a debit card: earthnode's paylink layer strips `debitcard`
  // from a fund request (VIOPayLinkManager::PAYMENT_METHOD_MAP) and rejects it when it is the only
  // method, because a debit card on a paylink is push-to-card, a payout. Debit stays a provide-mode
  // rail (AFT pull) and a valid request on Pay.
  const isRequestable = (selectionMethod: string): boolean => formType !== 'Collect' || selectionMethod !== 'debitcard';

  // Helper function to check if a payment method should be visible
  const isPaymentMethodVisible = (paymentMethod: PaymentMethodType): boolean => {
    if (paymentMethod === 'BankAccount' || paymentMethod === 'Email' || paymentMethod === 'PhoneNumber') {
      return true;
    }

    if (paymentMethod === 'DebitCard') {
      return true;
    }

    if (paymentMethod === 'PayPal' || paymentMethod === 'Venmo' || paymentMethod === 'Zelle') {
      return formType === 'Pay';
    }

    if (paymentMethod === 'CreditCard') {
      return formType === 'Collect';
    }

    return false;
  };

  return useMemo(() => {
    const paymentSelectionMethodOptions: PaymentSelectionMethodOption[] = [
      {
        label: t('paymentSelectionOption.bankAccount'),
        value: 'bank',
        featureKey: getPaymentMethodFeatureKey('BankAccount'),
        visible: true,
      },
      {
        label: 'Interac e-Transfer',
        renderLabel: <InteracWordmark type={formType === 'Pay' ? 'eTransfer' : 'moneyRequest'} />,
        value: 'email',
        featureKey: getPaymentMethodFeatureKey('Email'),
        visible: country === 'CA',
      },
      {
        label: t('paymentSelectionOption.creditCard'),
        value: 'credit',
        featureKey: getPaymentMethodFeatureKey('CreditCard'),
        visible: formType !== 'Pay',
      },
      {
        label: t('paymentSelectionOption.debitCard'),
        value: 'debitcard',
        featureKey: getPaymentMethodFeatureKey('DebitCard'),
        visible: true,
      },
      {
        label: t('paymentSelectionOption.paypal'),
        value: 'paypal',
        featureKey: getPaymentMethodFeatureKey('PayPal'),
        visible: formType === 'Pay',
      },
      {
        label: t('paymentSelectionOption.venmo'),
        value: 'venmo',
        featureKey: getPaymentMethodFeatureKey('Venmo'),
        visible: formType === 'Pay' && country === 'US',
      },
      {
        label: t('paymentSelectionOption.zelle', { defaultValue: 'Zelle' }),
        value: 'zelle',
        featureKey: getPaymentMethodFeatureKey('Zelle'),
        visible: formType === 'Pay' && country === 'US',
      },
    ];

    // Only ever requested from the contact, never provided on their behalf — a wallet credential
    // is the payer's own — so these join the requested-methods list alone and stay out of
    // paymentSelectionMethodOptions, which the "provide" select also renders from. Collect-only:
    // the paylink layer drops both wallets from a withdraw request, so on Pay they would be a dead
    // option the contact never sees.
    const walletRequestOptions: PaymentSelectionMethodOption[] = [
      {
        label: t('paymentSelectionOption.applePay', { defaultValue: 'Apple Pay' }),
        value: 'applepay',
        featureKey: getPaymentMethodFeatureKey('ApplePay'),
        visible: formType === 'Collect',
      },
      {
        label: t('paymentSelectionOption.googlePay', { defaultValue: 'Google Pay' }),
        value: 'googlepay',
        featureKey: getPaymentMethodFeatureKey('GooglePay'),
        visible: formType === 'Collect',
      },
    ];

    const bankingPaymentRailOptions: BankingPaymentRailOption[] = [
      {
        label: t('paymentRail.eft'),
        value: 'eft',
        featureKey: formType === 'Pay' ? 'EFTSendEnabled' : 'EFTCollectEnabled',
        visible: country === 'CA',
        isRecurringEnabled: true,
      },
      {
        label: t('paymentRail.vopayinstant'),
        value: 'vopayinstant',
        featureKey: 'RTRWithdrawEnabled',
        visible: country === 'CA' && formType === 'Pay',
        isRecurringEnabled: false,
      },
      {
        label: t('paymentRail.ach'),
        value: 'ach',
        featureKey: formType === 'Pay' ? 'ACHWithdrawEnabled' : 'ACHFundEnabled',
        visible: country === 'US',
        isRecurringEnabled: true,
      },
      {
        label: t('paymentRail.rtp'),
        value: 'rtp',
        featureKey: 'RTPWithdrawEnabled',
        visible: country === 'US' && formType === 'Pay',
        isRecurringEnabled: false,
      },
      {
        label: t('paymentRail.wire'),
        value: 'fedwire',
        featureKey: 'WireEnabled',
        visible: country === 'US' && formType === 'Pay',
        isRecurringEnabled: false,
      },
    ];

    return [
      // Connection Type Field (from useConnectionTypeFields)
      {
        label: () => t('Fields.paymentMethods.label'),
        fieldName: 'PaymentMethod',
        getInputState: () => 'required',
        showField: (values) => !!values.Contact && values.Contact !== 'new',
        serializeField: (ctx) => ctx.PaymentMethod,
        renderField: ({ getFormikData, ctx }) => {
          const fieldData = getFormikData<string>({});

          return (
            <Select
              version="2"
              {...fieldData}
              options={paymentMethods.filter(
                (option) =>
                  isPaymentMethodVisible(option.PaymentMethod) &&
                  isPaymentMethodAvailable(option.PaymentMethod, ctx.values.Currency) &&
                  (option.Currency === ctx.values.Currency || option.Currency === null) &&
                  (!clientAccountsEnabled || option.IsDefault),
              )}
              onChange={async (e, obj) => {
                fieldData.onChange(e);

                if (obj.data) {
                  const newValues = await handlePaymentMethodChange({
                    Token: obj.data.Token,
                    PaymentMethod: obj.data.PaymentMethod,
                    Value: obj.value,
                    Contact: ctx.values.Contact || '',
                  });

                  ctx.setValues({
                    ...ctx.values,
                    ...scheduleResetValues(),
                    ...newValues?.values,
                  });

                  ctx.setTouched({
                    ...ctx.touched,
                    ...SCHEDULE_RESET_TOUCHED,
                    ...newValues?.touched,
                  });

                  setIsNewPaymentMethod(false);
                } else if (obj.value === 'new' || obj.value === 'request') {
                  ctx.setValues({
                    ...ctx.values,
                    ...scheduleResetValues(),
                    EtransferConnectionType: 'email',
                    ProvideOrRequestPaymentInformation: obj.value === 'request' ? 'request' : 'provide',
                    Answer: '',
                    Question: '',
                    RecipientEmailAddress: '',
                    RecipientPhoneNumber: '',
                    FinancialInstitutionNumber: '',
                    BranchTransitNumber: '',
                    ABARoutingNumber: '',
                    AccountNumber: '',
                    PaymentSelectionMethod: '',
                    BankingPaymentRail: Country === 'CA' ? 'eft' : 'ach',
                    AccountSelectionMethod: '',
                    InvoiceNumber: '',
                    InvoiceFile: '',
                    InvoiceFileName: '',
                    RecipientMessage: '',
                  });

                  ctx.setTouched({
                    ...ctx.touched,
                    ...SCHEDULE_RESET_TOUCHED,
                    Answer: false,
                    Question: false,
                    RecipientEmailAddress: false,
                    RecipientPhoneNumber: false,
                    FinancialInstitutionNumber: false,
                    BranchTransitNumber: false,
                    ABARoutingNumber: false,
                    AccountNumber: false,
                    ProvideOrRequestPaymentInformation: false,
                    PaymentSelectionMethod: false,
                    BankingPaymentRail: false,
                    AccountSelectionMethod: false,
                    InvoiceNumber: false,
                    InvoiceFile: false,
                    InvoiceFileName: false,
                    RecipientMessage: false,
                  });
                }
              }}
              renderSelectedValue={(data, { Placeholder, value, disabled }) => {
                if (value === 'new') return t('Fields.paymentMethods.provideNewPaymentMethodLabel');
                if (value === 'request') return t('Fields.paymentMethods.requestPaymentMethodButton');

                if (!data) return <Placeholder />;

                return (
                  <div className={styles.paymentDetailsWrapper}>
                    <div className={styles.paymentDetailsItem}>
                      {data.PaymentMethod !== 'GooglePay' && (
                        <PaymentMethodLogo
                          method={data.PaymentMethod}
                          bankId={data.ExtraFields?.BankID}
                          creditCardBrand={data.ExtraFields?.CardBrand}
                          size="24px"
                          className={classes(disabled && 'iconDisabled')}
                        />
                      )}

                      <div className={styles.paymentDetails}>
                        <div className={styles.paymentName}>
                          {data.PaymentMethod === 'CreditCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'DebitCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'BankAccount' && data.ExtraFields.InstitutionName}{' '}
                          {data.PaymentDetails}
                        </div>

                        <div className={styles.paymentInfo}>
                          {data.PaymentMethod === 'CreditCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                          {data.PaymentMethod === 'DebitCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
              renderOption={(data, args) => {
                const featureKey = getPaymentMethodFeatureKey(data.PaymentMethod);

                return (
                  <SelectOptionLocked
                    {...args}
                    featureKey={featureKey}
                    onClick={() => {
                      setIsNewPaymentMethod(false);
                    }}
                  >
                    <div className={styles.paymentDetailsItem} style={{ maxWidth: '100%', overflow: 'hidden' }}>
                      {data.PaymentMethod !== 'GooglePay' && (
                        <PaymentMethodLogo
                          method={data.PaymentMethod}
                          bankId={data.ExtraFields?.BankID}
                          creditCardBrand={data.ExtraFields?.CardBrand}
                          size="24px"
                        />
                      )}

                      <div className={styles.paymentDetails}>
                        <div className={styles.paymentName}>
                          {data.PaymentMethod === 'CreditCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'DebitCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'BankAccount' && data.ExtraFields.InstitutionName}{' '}
                          {data.PaymentDetails}
                        </div>

                        <div className={styles.paymentInfo}>
                          {data.PaymentMethod === 'CreditCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                          {data.PaymentMethod === 'DebitCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                        </div>
                      </div>
                    </div>
                  </SelectOptionLocked>
                );
              }}
              optionValue={(o) => o.Token}
              renderDropdownHeader={
                clientAccountsEnabled
                  ? () => {
                      return (
                        <div className={styles.clientAccountPaymentMethodsHeader}>
                          <div className={styles.clientAccountPaymentMethodsTitle}>
                            {t('Fields.paymentMethods.clientAccountDescription.title')}
                          </div>

                          <BodyText size="sm" weight="md" className={styles.clientAccountPaymentMethodsInfo}>
                            {t('Fields.paymentMethods.clientAccountDescription.info')}{' '}
                            <a
                              href={`/client-accounts/${ctx.values.ClientAccount || ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.clientAccountPaymentMethodsLink}
                            >
                              {t('Fields.paymentMethods.clientAccountDescription.link')}
                            </a>{' '}
                          </BodyText>
                        </div>
                      );
                    }
                  : undefined
              }
              renderDropdownFooter={
                !clientAccountsEnabled
                  ? ({ close }) => (
                      <Box flx="row" gapSize="6px">
                        {permissions.ManageContacts && (
                          <Button
                            colorScheme="secondary"
                            size="small"
                            style={{
                              flex: '1',
                            }}
                            onClick={() => {
                              setIsNewPaymentMethod(true);
                              ctx.setFieldValue('PaymentMethod', 'new');

                              close();
                            }}
                            iconLeftProps={{ icon: 'article', fillAmount: 1 }}
                          >
                            {t('Fields.paymentMethods.addNewPaymentMethodButton', {
                              defaultValue: 'Provide Payment Method',
                            })}
                          </Button>
                        )}

                        {((formType === 'Collect' && permissions.CreateElinxCollect) ||
                          (formType === 'Pay' && permissions.CreateElinxPay)) && (
                          <Button
                            colorScheme="secondary"
                            size="small"
                            style={{
                              flex: '1',
                            }}
                            onClick={() => {
                              setIsNewPaymentMethod(true);
                              ctx.setFieldValue('PaymentMethod', 'request');

                              close();
                            }}
                            iconLeft="call_received"
                          >
                            {t('Fields.paymentMethods.requestPaymentMethodButton', {
                              defaultValue: 'Request Payment Method',
                            })}
                          </Button>
                        )}
                      </Box>
                    )
                  : undefined
              }
            />
          );
        },

        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.bankingPaymentRail.label'),
        fieldName: 'BankingPaymentRail',
        getInputState: () => 'required',
        showField: (values) =>
          values.PaymentSelectionMethod === 'bank' && values.Contact !== 'new' && !isNewPaymentMethod,
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return (
            <Select
              version="2"
              placeholder={t('Fields.paymentSelectionMethod.placeholder')}
              options={bankingPaymentRailOptions.filter((option) => option.visible)}
              sort={false}
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.label}
                </SelectOptionLocked>
              )}
              {...fieldData}
              onChange={async (e, obj) => {
                fieldData.onChange(e);
                if (!obj.value) return;

                if (obj.value === 'fedwire') {
                  const bankAccountsResponse = await fetchClientBankAccounts({
                    Token: ctx.values.PaymentMethod,
                  });

                  const bankInfo = Object.values(bankAccountsResponse.BankAccounts).find(
                    (bank) => ctx.values.PaymentMethod === bank.Token,
                  );

                  if (!bankInfo) return;

                  ctx.setValues({
                    ...ctx.values,
                    BankingPaymentRail: obj.value,
                    CompanyName: bankInfo.AccountHolderName,
                  });

                  ctx.setTouched({
                    ...ctx.touched,
                    Answer: false,
                    ClientReferenceNumber: false,
                    Question: false,
                    RecipientEmailAddress: false,
                    RecipientName: false,
                    RecipientPhoneNumber: false,
                  });
                }
              }}
            />
          );
        },
        validate: () =>
          Yup.string().when(['PaymentSelectionMethod'], {
            is: (PaymentSelectionMethod: string) => PaymentSelectionMethod === 'bank',
            then: Yup.string().required(),
            otherwise: Yup.string(),
          }),
      },
      {
        fieldName: 'ProvideOrRequestPaymentInformation',
        label: () => t('Fields.provideOrRequestPaymentInformation.label'),
        getInputState: () => 'optional',
        showField: (values) =>
          values.Contact === 'new' &&
          !!PayLinkEnabled &&
          !!(
            permissions.ManageContacts ||
            (formType === 'Collect' && permissions.CreateElinxCollect) ||
            (formType === 'Pay' && permissions.CreateElinxPay)
          ),
        renderField: ({ ctx, getFormikData }) => {
          return (
            <Radio
              {...getFormikData<string>({
                onChange: (e) => {
                  if (ctx.values.ProvideOrRequestPaymentInformation !== e.target.value) {
                    ctx.setFieldValue('ProvideOrRequestPaymentInformation', e.target.value);
                    ctx.setFieldValue('PaymentSelectionMethod', '');
                    ctx.setFieldTouched('PaymentSelectionMethod', false);
                  }
                },
              })}
              variant="outlineShadow"
              noOrdering
              options={{
                ...(permissions.ManageContacts && { provide: t('provideOrRequestInfoOptions.provide') }),
                ...(((formType === 'Collect' && permissions.CreateElinxCollect) ||
                  (formType === 'Pay' && permissions.CreateElinxPay)) && {
                  request: t('provideOrRequestInfoOptions.request'),
                }),
              }}
            />
          );
        },
      },

      {
        label: () => t('Fields.paymentSelectionMethod.labelRequested'),
        fieldName: 'PaymentSelectionMethod',
        getInputState: () => 'required',
        showField: (values) =>
          !!PayLinkEnabled &&
          values.ProvideOrRequestPaymentInformation === 'request' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        serializeField: (values) =>
          values.PaymentSelectionMethod?.split(',')
            .filter((paymentMethod) => paymentMethod)
            .join(','),
        tooltip: () => (
          <Tooltip
            information={
              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>{t('Fields.paymentSelectionMethod.tooltipHeader')}</h3>
                <div>{t('Fields.paymentSelectionMethod.tooltipInfo')}</div>
              </Box>
            }
            position="top"
            width="394px"
          />
        ),
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string[], string>({
            formatValue: (value) => (value ? value.split(',') : []),
          });

          return (
            <MultiselectOutsideOptions
              placeholder={t('Fields.paymentSelectionMethod.placeholder')}
              options={(isToday(ctx.values.StartDate || '') && ctx.values.ScheduleType === 'single'
                ? [...paymentSelectionMethodOptions, ...walletRequestOptions].filter((option) => option.visible)
                : paymentSelectionMethodOptions.filter((option) => option.visible && option.value === 'bank')
              ).filter(
                (option) =>
                  isRequestable(option.value) &&
                  isSelectionMethodAvailable(option.value, ctx.values.Currency, { isRequested: true }),
              )}
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.renderLabel || option.label}
                </SelectOptionLocked>
              )}
              sort={false}
              {...fieldData}
            />
          );
        },

        validate: () => Yup.mixed().required(),
      },

      {
        label: () => t('Fields.paymentSelectionMethod.label'),
        fieldName: 'PaymentSelectionMethod',
        getInputState: () => 'required',
        showField: (values) =>
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        helpText: (values: PaymentFieldDefault) => {
          if (
            (values.PaymentSelectionMethod === 'credit' || values.PaymentSelectionMethod === 'debitcard') &&
            values.PaymentMethod !== 'request'
          ) {
            return t('Fields.paymentSelectionMethod.helpText');
          }

          if (values.PaymentSelectionMethod === 'paypal' && values.PaymentMethod !== 'request') {
            return t('Fields.paymentSelectionMethod.helpTextPayPal');
          }

          if (values.PaymentSelectionMethod === 'venmo' && values.PaymentMethod !== 'request') {
            return t('Fields.paymentSelectionMethod.helpTextVenmo');
          }

          if (values.PaymentSelectionMethod === 'zelle' && values.PaymentMethod !== 'request') {
            return t('Fields.paymentSelectionMethod.helpTextZelle', {
              defaultValue: "You'll be asked to enter your Zelle details after clicking Submit",
            });
          }

          return null;
        },
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return (
            <Select
              version="2"
              {...fieldData}
              options={paymentSelectionMethodOptions.filter(
                (option) => option.visible && isSelectionMethodAvailable(option.value, ctx.values.Currency),
              )}
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.renderLabel || option.label}
                </SelectOptionLocked>
              )}
            />
          );
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.bankingPaymentRail.label'),
        fieldName: 'BankingPaymentRail',
        getInputState: () => 'required',
        showField: (values) =>
          !!values.PaymentSelectionMethod?.includes('bank') &&
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        serializeField: (values) => values.BankingPaymentRail,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return (
            <Select
              version="2"
              placeholder={t('Fields.bankingPaymentRail.placeholder')}
              options={bankingPaymentRailOptions.filter((option) => option.visible)}
              sort={false}
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.label}
                </SelectOptionLocked>
              )}
              {...fieldData}
            />
          );
        },
        validate: (ctx) => (ctx.values.PaymentSelectionMethod?.includes('bank') ? Yup.mixed().required() : Yup.mixed()),
      },
      {
        label: () => t('Fields.cancellationPeriod.label'),
        fieldName: 'CancellationPeriod',
        getInputState: () => 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'email' && ctx.ProvideOrRequestPaymentInformation === 'provide',
        serializeField: (ctx) => ctx.CancellationPeriod,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return <TextField {...fieldData} variant="outlineShadow" preset="number" placeholder="" />;
        },
        validate: () =>
          Yup.string().test('cancellation-period-validation', t('Fields.cancellationPeriod.validation'), (value) => {
            const periodValidation = Number(value) > 30;

            return !periodValidation;
          }),
      },
      {
        label: () => t('Fields.receiverPhoneNumber.label'),
        fieldName: 'ReceiverPhoneNumber',
        getInputState: () => 'required',
        showField: (values) =>
          values.PaymentMethod === 'request' &&
          values.ProvideOrRequestPaymentInformation === 'request' &&
          !!values.PaymentSelectionMethod &&
          values.PaymentSelectionMethod.includes('bank') &&
          country === 'US',
        serializeField: (values) => NumbersOnly(values.ReceiverPhoneNumber || ''),
        tooltip: () => (
          <Tooltip
            information={<div>{t('Fields.receiverPhoneNumber.tooltipInfo')}</div>}
            position="top"
            width="298px"
          />
        ),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
        },
        validate: () => Yup.string().required(),
      },
    ];
  }, [formType, country, paymentMethods, isNewPaymentMethod]);
}
